import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import { CatmullRomCurve3, Vector3, type Group } from 'three'
import { ChamasMesa3D } from './ChamasMesa3D'
import { BolaDeFogo3D } from '../../zonas/BolaDeFogo3D'
import type { VetorTabletop } from '../../../dominio/tabletop'
import { PecaDetalhe3D as Peca } from './PecaDetalhe3D'

export type TipoRiscoMesa = 'vazamento' | 'liquidoDerramado' | 'fogo' | 'fogoTanque' | 'curtoCircuito' | 'bleve'

function Poca3D() {
  return (
    <group name="poca-liquido">
      {[[0, 0, 1], [0.65, 0.25, 0.6], [-0.55, -0.3, 0.55]].map(([x, z, escala], i) => (
        <mesh key={i} position={[x!, 0.065 + i * 0.002, z!]} scale={[escala!, 0.022, escala! * 0.65]}>
          <sphereGeometry args={[1.1, 20, 10]} />
          <meshStandardMaterial color="#528b83" roughness={0.18} metalness={0.25} />
        </mesh>
      ))}
    </group>
  )
}

/** Marcadores visuais independentes; os cálculos continuam vinculados à fonte de produto. */
export function FiguraRisco3D({ tipo, alturaOrigem = 1.25, direcaoSaida = [0, 0, 1], vinculado = false }: { tipo: TipoRiscoMesa; alturaOrigem?: number; direcaoSaida?: VetorTabletop; vinculado?: boolean }) {
  const efeito = useRef<Group>(null)
  const altura = Math.max(.08, alturaOrigem)
  const curva = useMemo(() => new CatmullRomCurve3([
    new Vector3(0, 0, 0),
    new Vector3(direcaoSaida[0] * .35, Math.min(0, direcaoSaida[1] * .35) - .08, direcaoSaida[2] * .35),
    new Vector3(direcaoSaida[0] * .65, -altura * .5, direcaoSaida[2] * .65),
    new Vector3(direcaoSaida[0] * .9, -altura + .04, direcaoSaida[2] * .9),
  ]), [altura, direcaoSaida[0], direcaoSaida[1], direcaoSaida[2]])
  useFrame(({ clock }) => {
    if (!efeito.current) return
    const t = clock.getElapsedTime()
    if (tipo === 'vazamento') efeito.current.children.forEach((m, i) => {
      const p = curva.getPoint((t * .6 + i / 8) % 1)
      m.position.copy(p)
    })
    if (tipo === 'curtoCircuito') efeito.current.scale.setScalar(0.6 + Math.abs(Math.sin(t * 13)) * 0.4)
  })
  if (tipo === 'bleve') return <group name="bleve-ilustrativo"><BolaDeFogo3D estado={{ diametroM: 12, alturaCentroM: 6, intensidade: 1 }} /></group>
  if (tipo === 'fogo' || tipo === 'fogoTanque') return <ChamasMesa3D jato={tipo === 'fogoTanque'} />
  if (tipo === 'liquidoDerramado') return <Poca3D />
  if (tipo === 'vazamento') return (
    <group name="vazamento-liquido">
      <group position={[0, vinculado ? 0 : 1.25, 0]}>
        {!vinculado && <Peca posicao={[0, 0, -.1]} tamanho={[.22, .16, .22]} cor="#697781" metal={.65} raio={.025} />}
        <mesh>
          <tubeGeometry args={[curva, 24, .045, 8, false]} />
          <meshStandardMaterial color="#72b8ba" transparent opacity={.8} roughness={.15} />
        </mesh>
        <group ref={efeito}>
          {Array.from({ length: 8 }, (_, i) => (
            <mesh key={i} scale={[.65, 1.4, .65]}>
              <sphereGeometry args={[.035, 8, 6]} />
              <meshStandardMaterial color="#91d5d6" roughness={.2} />
            </mesh>
          ))}
        </group>
        <group position={[direcaoSaida[0] * .9, -altura, direcaoSaida[2] * .9]}><Poca3D /></group>
      </group>
    </group>
  )
  return (
    <group name="curto-circuito">
      <Peca posicao={[0, 0.25, 0]} tamanho={[0.32, 0.46, 0.2]} cor="#48525c" raio={0.025} />
      {[-1, 1].map((lado) => (
        <Peca key={lado} posicao={[lado * 0.22, 0.18, 0.08]} tamanho={[0.3, 0.025, 0.025]} cor="#242c34" rotacao={[0, 0, lado * 0.35]} raio={0.006} />
      ))}
      <group ref={efeito} position={[0, 0.5, 0.14]} name="faiscas-eletricas">
        {Array.from({ length: 8 }, (_, i) => (
          <group key={i} rotation={[0, 0, i * Math.PI / 4]}>
            <Peca posicao={[0, 0.17, 0]} tamanho={[0.014, 0.17, 0.015]} cor="#b9e9ff" realce={{ emissive: '#79cfff', emissiveIntensity: 1.4 }} raio={0.003} rotacao={[0, 0, 0.3]} />
            <Peca posicao={[0.045, 0.29, 0]} tamanho={[0.014, 0.12, 0.015]} cor="#ffda73" realce={{ emissive: '#ffd75e', emissiveIntensity: 1.2 }} raio={0.003} rotacao={[0, 0, -0.5]} />
          </group>
        ))}
      </group>
    </group>
  )
}
